import { LinearGradient } from 'expo-linear-gradient';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import theme from 'theme';
import { ProgramType } from 'utils/enum';
import { calcSizeForDevice, calcSizeForTv } from 'utils/index';

import { useAutoNext } from '../AutoNext';
import { VideoInfoView } from '../Video/UI/Info';
import { useSubtitlesSettings } from '../Video/UI/hooks/SubtitlesContext';
import { usePlayer } from '../context';

import Button from 'components/Button';
import { Icons } from 'components/Icons';
import { useProgram } from 'components/Program/context';
import { WebDiv } from 'components/WebDiv';

enum Actions {
  PLAY = 'play',
  PAUSE = 'pause',
  LIVE_REAL_TIME = 'liveRealTime',
  FORWARD = 'forward',
  REWIND = 'rewind',
  CURRENT_TIME = 'currentTime',
  SET_ACTIVE_SUBTITLE = 'setActiveSubtitle',
  HIDE_SUBTITLES = 'hideSubtitles',
}

const LIVE_SKIP_SECONDS = 10;

export type SpallaLanguageCode = 'pt-br' | 'en' | 'es' | 'fr' | 'zh' | 'it';

export const spallaLanguages: Record<SpallaLanguageCode, string> = {
  'pt-br': 'Português',
  en: 'English',
  es: 'Español',
  fr: 'French',
  zh: 'Chinese',
  it: 'Italiano',
};

const CTVSpallaControls = (): React.ReactPortal | null => {
  const program = useProgram();
  const {
    ref: spallaRef,
    setIsVisible,
    play,
    pause,
    isPlaying,
    isVisible,
    isLiveRealTime,
    setFullScreen,
    spallaConfigRef,
    setTextTrack,
    textTracks,
    textTrack: currentTextTrack,
    isSpallaReady,
    nextTrack,
  } = usePlayer();
  const { setEnabled } = useSubtitlesSettings();

  const [container, setContainer] = useState<Element | null>(null);
  const [isNavigatingMenu, setIsNavigatingMenu] = useState<boolean>(false);
  const [iframe, setIframe] = useState<HTMLIFrameElement | null>(null);
  const BROWSING_MENU_TIMER = useRef<number | null>(null);
  const isNavigatingMenuRef = useRef<boolean>(false);
  const isPlayingRef = useRef<boolean>(false);
  const sliderRef = React.useRef<HTMLDivElement>(null);
  const [isSubtitleSelecionOpen, setIsSubtitleSelecionOpen] = React.useState(false);
  const [isAudioSelectionOpen, setIsAudioSelectionOpen] = React.useState(false);
  const [isBrowsingMenu, setIsBrowsingMenu] = React.useState(true);

  const toggleAudioSelection = () => setIsAudioSelectionOpen(!isAudioSelectionOpen);
  const toggleSubtitleSelection = () => setIsSubtitleSelecionOpen(!isSubtitleSelecionOpen);

  const isLive = program.type === ProgramType.LIVE || program.type == ProgramType.RADIO;
  const currentTime = spallaConfigRef.current.progress;
  const duration = spallaConfigRef.current.duration;

  const { autoNextCard, showNextTrack } = useAutoNext({ currentTime, duration });

  const isPlayPauseFocused = () => document.querySelector('.player-play-pause-button .focused');

  const availableAudioTracks = React.useMemo(() => {
    const audio = [];
    const videoId = spallaConfigRef.current.videoId;
    const videoIdDub = spallaConfigRef.current.videoIdDub;

    if (videoId?.trim()) {
      audio.push({
        id: videoId,
        title: 'Original',
      });
    }

    if (videoIdDub?.trim()) {
      audio.push({
        id: videoIdDub,
        title: 'Dublado',
      });
    }

    return audio;
  }, [spallaConfigRef.current.videoId, spallaConfigRef.current.videoIdDub]);

  const formattedCurrentTime = React.useMemo(
    () => new Date(currentTime * 1000).toISOString().slice(11, 19),
    [currentTime]
  );

  const formattedDuration = React.useMemo(
    () => new Date(duration * 1000).toISOString().slice(11, 19),
    [duration]
  );

  const currentTimePercentage = React.useMemo(
    () => ((currentTime * 100) / duration).toFixed(2),
    [currentTime, duration]
  );

  const sliderOnKeyPress = React.useCallback(
    (e: KeyboardEvent) => {
      //left
      if (e.keyCode === 37) {
        const newCurrentTime = currentTime - 15;

        if (newCurrentTime < 0) {
          sendAction({ action: Actions.CURRENT_TIME, currentTime: 0 });
          return;
        }

        sendAction({ action: Actions.REWIND, seconds: 15 });
        return;
      }

      //right
      if (e.keyCode === 39) {
        const newCurrentTime = currentTime + 15;

        if (newCurrentTime > duration) {
          sendAction({ action: Actions.CURRENT_TIME, currentTime: duration });
          return;
        }

        sendAction({ action: Actions.FORWARD, seconds: 15 });
      }
    },
    [duration, currentTime]
  );

  const togglePlay = () => {
    if (isPlaying) {
      pause();
      return;
    }

    play();
  };

  const onKeyPress = React.useCallback(
    (e: KeyboardEvent) => {
      const playPauseFocused = isPlayPauseFocused();

      const isBack = [8, 27, 461, 10009].includes(e.keyCode);
      const isEnterOrPlayPause = [13, 10252].includes(e.keyCode);

      if (isBack && !isBrowsingMenu) {
        e.preventDefault();
        e.stopPropagation();
        handleBack();
        return;
      }

      if (isEnterOrPlayPause && !isBrowsingMenu && (playPauseFocused || isPlaying)) {
        e.preventDefault();
        e.stopPropagation();
        togglePlay();
        return;
      }

      if (isPlaying && !isBrowsingMenu) setIsBrowsingMenu(true);
      if (sliderRef.current?.classList.contains('focused')) sliderOnKeyPress(e);
    },
    [isPlaying, isBrowsingMenu, sliderOnKeyPress, duration]
  );

  // append keypress listener
  useEffect(() => {
    window.addEventListener('keydown', onKeyPress, false);
    return () => window.removeEventListener('keydown', onKeyPress);
  }, [onKeyPress]);

  useEffect(() => {
    const spallaPlayer = spallaRef?.current;
    if (!(spallaPlayer instanceof HTMLElement)) return;

    if (isLive) {
      setIframe(spallaPlayer?.querySelector('iframe'));
      return;
    }

    const observer = new MutationObserver(() => {
      const iframeElement = spallaPlayer.querySelector('iframe');

      const isVisible = iframeElement !== null;

      const nextState = isVisible ? iframeElement : null;

      setIframe((prev) => {
        if (prev !== nextState) {
          return nextState;
        }
        return prev;
      });
    });

    const initialIframe = spallaPlayer.querySelector('iframe');
    const initialVisible = initialIframe !== null && initialIframe.offsetParent !== null;
    setIframe(initialVisible ? initialIframe : null);

    observer.observe(spallaPlayer, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    return () => observer.disconnect();
  }, []);

  const sendAction = ({
    action,
    seconds,
    currentTime,
    language,
  }: {
    action: Actions;
    seconds?: number;
    currentTime?: number;
    language?: string;
  }) => {
    switch (action) {
      case Actions.PLAY:
        iframe?.contentWindow?.postMessage({ action }, '*');
        play();
        break;
      case Actions.PAUSE:
        iframe?.contentWindow?.postMessage({ action }, '*');
        pause();
        break;
      case Actions.FORWARD:
        iframe?.contentWindow?.postMessage({ action, seconds }, '*');
        break;
      case Actions.REWIND:
        iframe?.contentWindow?.postMessage({ action, seconds }, '*');
        break;
      case Actions.CURRENT_TIME:
        iframe?.contentWindow?.postMessage({ action, currentTime }, '*');
        break;
      case Actions.SET_ACTIVE_SUBTITLE:
        iframe?.contentWindow?.postMessage({ action, language }, '*');
        break;
      case Actions.HIDE_SUBTITLES:
        iframe?.contentWindow?.postMessage({ action }, '*');
        break;
      case Actions.LIVE_REAL_TIME:
        iframe?.contentWindow?.postMessage({ action }, '*');
        break;
    }
  };

  const togglePlayPause = useCallback(() => {
    const action = isPlaying ? Actions.PAUSE : Actions.PLAY;
    sendAction({ action });
  }, [isPlaying, iframe, play, pause]);

  const handleRewind = useCallback(() => {
    sendAction({ action: Actions.REWIND, seconds: LIVE_SKIP_SECONDS });
  }, [sendAction]);
  const handleForward = useCallback(() => {
    sendAction({ action: Actions.FORWARD, seconds: LIVE_SKIP_SECONDS });
  }, [sendAction]);

  const handleLiveRealTime = () => {
    sendAction({ action: Actions.LIVE_REAL_TIME });
    sendAction({ action: Actions.PLAY });
  };

  const handleBack = useCallback(() => {
    setFullScreen(false);
    setIsVisible(false);
    sendAction({ action: Actions.PAUSE });
  }, [setIsVisible, pause]);

  useEffect(() => {
    const spallaPlayer = spallaRef?.current as HTMLDivElement;

    if (spallaPlayer) {
      let spallaControls = spallaPlayer?.parentNode?.querySelector?.('#spalla-ctv-controls');

      if (!spallaControls) {
        spallaControls = document.createElement('div');
        spallaControls.id = 'spalla-ctv-controls';
        spallaPlayer?.parentNode?.appendChild(spallaControls);
      }

      setContainer(spallaControls);
    }
  }, [spallaRef]);

  useEffect(() => {
    sendAction({ action: isVisible ? Actions.PLAY : Actions.PAUSE });
  }, [isVisible, isSpallaReady]);

  const showControls = useCallback(() => {
    setIsNavigatingMenu(true);
    isNavigatingMenuRef.current = true;
    if (BROWSING_MENU_TIMER.current) clearTimeout(BROWSING_MENU_TIMER.current);
    BROWSING_MENU_TIMER.current = window.setTimeout(() => {
      setIsNavigatingMenu(false);
      isNavigatingMenuRef.current = false;
      setIsAudioSelectionOpen(false);
      setIsSubtitleSelecionOpen(false);
    }, 3000);
  }, []);

  useEffect(() => {
    isPlayingRef.current = isPlaying ?? false;
  }, [isPlaying]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      const playPauseFocused = isPlayPauseFocused();

      const isBack = [8, 27, 461, 10009].includes(e.keyCode);
      const isEnterOrPlayPause = [13, 10252].includes(e.keyCode);

      if (isBack && !isNavigatingMenuRef.current) {
        e.preventDefault();
        e.stopPropagation();
        handleBack();
        return;
      }

      if (
        isEnterOrPlayPause &&
        !isNavigatingMenuRef.current &&
        (playPauseFocused || isPlayingRef.current)
      ) {
        e.preventDefault();
        e.stopPropagation();
        togglePlayPause();
        return;
      }

      showControls();
    };

    window.addEventListener('keydown', handleKeyPress, false);
    return () => {
      window.removeEventListener('keydown', handleKeyPress, false);
      if (BROWSING_MENU_TIMER.current) clearTimeout(BROWSING_MENU_TIMER.current);
    };
  }, [showControls, handleBack, togglePlayPause]);

  const TogglePlayPauseIcon = isPlaying ? Icons.SolidPause : Icons.Play;

  if (!container) return null;

  if (isPlaying && !isNavigatingMenu && !showNextTrack) {
    return createPortal(
      <WebDiv className="navigation-container navigation-priority">
        <Pressable accessibilityRole="button" onPress={() => {}} />
      </WebDiv>,
      container
    );
  }

  return createPortal(
    <WebDiv className={'navigation-container navigation-priority player-controls-container'}>
      <View style={styles.controlsContainer}>
        <LinearGradient
          colors={['transparent', '#000000bc']}
          locations={[0.1, 0.4]}
          start={{ x: 0, y: 1 }}
          end={{ x: 0, y: 0 }}
          style={styles.topControlsContainer}>
          <Pressable
            accessibilityRole="button"
            role="button"
            onPress={handleBack}
            style={styles.backButton}>
            <Icons.TurnBack
              width={calcSizeForTv(60).toString()}
              height={calcSizeForTv(60).toString()}
            />
          </Pressable>

          <VideoInfoView
            title={program?.title}
            ratingValue={program?.rating}
            ratingDescription={program?.rateDescription}
          />
        </LinearGradient>

        {autoNextCard}

        <LinearGradient
          colors={['#000000bc', showNextTrack && nextTrack ? '#000000bc' : 'transparent']}
          locations={[0.4, 0.8]}
          start={{ x: 0, y: 1 }}
          end={{ x: 0, y: 0 }}
          style={styles.bottomControlsContainer}>
          <WebDiv
            ref={sliderRef}
            className={
              isLive
                ? 'navigation container navigation-ignore'
                : 'touchable ignore-left-key ignore-right-key'
            }>
            {duration && currentTime && !isLive ? (
              <View style={styles.timeContainer}>
                <Text style={styles.timeText}>{formattedCurrentTime}</Text>

                <Text style={styles.timeText}>{formattedDuration}</Text>
              </View>
            ) : null}

            <View style={styles.progressBarContainer}>
              <View
                style={[
                  styles.progressBar,
                  {
                    /**
                     * Fix the progress bar width type issue by converting the currentTimePercentage to a string
                     * and appending a '%' symbol to it.
                     */
                    backgroundColor: isLive ? theme.colors.error[500] : theme.colors.primary[500],
                    width: isLive ? '100%' : parseFloat(currentTimePercentage) + '%',
                    zIndex: 10,
                  } as any, // Type assertion to handle percentage string
                ]}
              />

              <View
                style={[
                  styles.progressBackground,
                  {
                    backgroundColor: theme.colors.primary[100],
                  },
                ]}
              />
            </View>
          </WebDiv>
          <View style={[styles.controlsRow, { marginBottom: calcSizeForTv(10) }]}>
            <WebDiv className="player-play-pause-button">
              <Pressable
                accessibilityRole="button"
                onPress={togglePlayPause}
                style={styles.playPauseButton}>
                <TogglePlayPauseIcon
                  width={calcSizeForTv(40).toString()}
                  height={calcSizeForTv(40).toString()}
                />
              </Pressable>
            </WebDiv>

            {isLive && (
              <Pressable
                accessibilityRole="button"
                onPress={handleRewind}
                style={styles.skipButton}>
                <Icons.Back10Secs
                  width={calcSizeForTv(40).toString()}
                  height={calcSizeForTv(40).toString()}
                />
              </Pressable>
            )}
            {isLive && (
              <Pressable
                accessibilityRole="button"
                onPress={handleForward}
                style={styles.skipButton}>
                <Icons.Foward10Secs
                  width={calcSizeForTv(40).toString()}
                  height={calcSizeForTv(40).toString()}
                />
              </Pressable>
            )}

            {isLive && (
              <Pressable
                accessibilityRole="button"
                style={styles.liveRealTimeButton}
                onPress={handleLiveRealTime}>
                <Icons.SolidCircle
                  color={isLiveRealTime ? 'red' : '#888'}
                  width={calcSizeForTv(24).toString()}
                  height={calcSizeForTv(24).toString()}
                />
                <Text style={styles.liveRealTimeButtonText}>Ao vivo</Text>
              </Pressable>
            )}

            {!!availableAudioTracks?.length && (
              <Pressable
                accessibilityRole="button"
                onPress={toggleAudioSelection}
                style={styles.audioButton}>
                <Icons.AudioLibrary
                  width={calcSizeForDevice(40).toString()}
                  height={calcSizeForDevice(40).toString()}
                />
              </Pressable>
            )}

            {!!textTracks?.length && (
              <Pressable accessibilityRole="button" onPress={toggleSubtitleSelection}>
                <Icons.Subtitles
                  width={calcSizeForDevice(40).toString()}
                  height={calcSizeForDevice(40).toString()}
                />
              </Pressable>
            )}
          </View>
        </LinearGradient>
      </View>

      {isAudioSelectionOpen && (
        <View
          style={{
            padding: 16,
            zIndex: 200,
            position: 'absolute',
            bottom: 0,
            width: '100%',
            backgroundColor: 'rgba(0,0,0,0.8)',
          }}>
          <WebDiv className="navigation-container navigation-priority">
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
              <Pressable accessibilityRole="button" onPress={toggleAudioSelection}>
                <Icons.ArrowLeft
                  width={calcSizeForDevice(40).toString()}
                  height={calcSizeForDevice(40).toString()}
                />
              </Pressable>

              <Text style={{ color: 'white', fontFamily: theme.fonts.inter.bold, fontSize: 18 }}>
                Seleção de áudio
              </Text>
            </View>

            <View style={{ flexDirection: 'row', gap: 16, marginTop: 16 }}>
              {availableAudioTracks.map((audio: any) => {
                return (
                  <Button
                    variant="solid"
                    role="button"
                    style={{ justifyContent: 'center' }}
                    onPress={() => {
                      if (spallaConfigRef.current.currentVideoId === audio.id) return;
                      spallaConfigRef.current.currentVideoId = audio.id;
                      if (BROWSING_MENU_TIMER.current) clearTimeout(BROWSING_MENU_TIMER.current);
                      BROWSING_MENU_TIMER.current = window.setTimeout(() => {
                        setIsNavigatingMenu(false);
                        isNavigatingMenuRef.current = false;
                        setIsAudioSelectionOpen(false);
                        setIsSubtitleSelecionOpen(false);
                      }, 3000);
                    }}>
                    {audio.title}
                  </Button>
                );
              })}
            </View>
          </WebDiv>
        </View>
      )}

      {isSubtitleSelecionOpen && (
        <View
          style={{
            padding: 16,
            zIndex: 200,
            position: 'absolute',
            bottom: 0,
            width: '100%',
            backgroundColor: 'rgba(0,0,0,0.8)',
          }}>
          <WebDiv className="navigation-container navigation-priority">
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
              <Pressable accessibilityRole="button" onPress={toggleSubtitleSelection}>
                <Icons.ArrowLeft
                  width={calcSizeForDevice(40).toString()}
                  height={calcSizeForDevice(40).toString()}
                />
              </Pressable>

              <Text style={{ color: 'white', fontFamily: theme.fonts.inter.bold, fontSize: 18 }}>
                Seleção de legenda
              </Text>
            </View>

            <View style={{ flexDirection: 'row', gap: 16, marginTop: 16 }}>
              {!!textTracks?.length && (
                <>
                  {textTracks?.map((textTrack: any) => {
                    return (
                      <Button
                        key={textTrack.id}
                        role="button"
                        variant="solid"
                        style={{ justifyContent: 'center' }}
                        onPress={() => {
                          if (textTrack == currentTextTrack) return;
                          toggleSubtitleSelection();
                          setEnabled(true);
                          setTextTrack(textTrack);
                          sendAction({
                            action: Actions.SET_ACTIVE_SUBTITLE,
                            language: spallaLanguages[textTrack.language as SpallaLanguageCode],
                          });
                        }}>
                        {textTrack.title}
                      </Button>
                    );
                  })}
                  <Button
                    role="button"
                    variant="solid"
                    style={{ justifyContent: 'center' }}
                    onPress={() => {
                      toggleSubtitleSelection();
                      setEnabled(false);
                      setTextTrack(undefined);
                      sendAction({ action: Actions.HIDE_SUBTITLES });
                    }}>
                    Desativar Legenda
                  </Button>
                </>
              )}
            </View>
          </WebDiv>
        </View>
      )}
    </WebDiv>,
    container
  );
};

const styles = StyleSheet.create({
  controlsContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    zIndex: 100,
  },
  topControlsContainer: {
    flexDirection: 'row',
    position: 'relative',
    alignItems: 'center',
    padding: calcSizeForTv(24),
  },
  bottomControlsContainer: {
    flexDirection: 'column',
    justifyContent: 'flex-end',
    position: 'relative',
    marginTop: 'auto',
    height: calcSizeForTv(200),
    padding: calcSizeForTv(32),
  },
  backButton: {
    marginRight: calcSizeForTv(40),
    padding: calcSizeForTv(4),
  },
  liveProgressBar: {
    height: calcSizeForTv(8),
    width: '100%',
    backgroundColor: 'red',
    paddingHorizontal: calcSizeForTv(16),
    marginBottom: calcSizeForTv(32),
  },
  playPauseButton: {
    padding: calcSizeForTv(8),
  },
  skipButton: {
    padding: calcSizeForTv(8),
  },
  liveRealTimeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderStyle: 'solid',
    borderWidth: calcSizeForTv(2),
    borderColor: 'red',
    borderRadius: 10,
    paddingHorizontal: calcSizeForTv(24),
    paddingVertical: calcSizeForTv(12),
  },
  liveRealTimeButtonText: {
    justifyContent: 'center',
    alignItems: 'center',
    color: 'white',
    marginLeft: calcSizeForTv(10),
    fontSize: calcSizeForTv(24),
  },
  timeContainer: {
    margin: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  timeText: {
    color: 'white',
    fontFamily: theme.fonts.inter.bold,
    fontSize: 18,
  },
  progressBarContainer: {
    margin: 16,
    height: 5,
    position: 'relative',
    borderRadius: 9999,
    overflow: 'hidden',
  },
  progressBar: {
    height: 5,
    position: 'absolute',
  },
  progressBackground: {
    height: 5,
    width: '100%',
    zIndex: 5,
    borderRadius: 9999,
    position: 'absolute',
  },
  controlsRow: {
    flexDirection: 'row',
    gap: calcSizeForTv(42),
    padding: calcSizeForTv(16),
  },
  audioButton: {
    marginLeft: 'auto',
  },
});

export default CTVSpallaControls;
